import axios from "axios";
import env from "../config/env";

// Configuración de reintentos para Azure Functions
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 segundo
  maxDelay: 10000, // 10 segundos
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  retryableErrors: ['ECONNABORTED', 'ETIMEDOUT', 'ENOTFOUND']
};

// Función para calcular delay exponencial con jitter
const calculateDelay = (attempt, baseDelay = RETRY_CONFIG.baseDelay) => {
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 1000; // Añadir jitter para evitar thundering herd
  return Math.min(exponentialDelay + jitter, RETRY_CONFIG.maxDelay);
};

// Función para determinar si un error es reintentable
const isRetryableError = (error) => {
  if (!error.response && !error.request) return false;
  
  // Errores de red
  if (error.code && RETRY_CONFIG.retryableErrors.includes(error.code)) {
    return true;
  }
  
  // Errores HTTP específicos
  if (error.response && RETRY_CONFIG.retryableStatusCodes.includes(error.response.status)) {
    return true;
  }
  
  // Timeout errors
  if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
    return true;
  }
  
  return false;
};

// Función principal de reintentos
const retryRequest = async (config, attempt = 0) => {
  try {
    const response = await axios(config);
    return response;
  } catch (error) {
    const isLastAttempt = attempt >= RETRY_CONFIG.maxRetries;
    const shouldRetry = isRetryableError(error) && !isLastAttempt;
    
    if (shouldRetry) {
      const delay = calculateDelay(attempt);
      console.warn(`Request failed (attempt ${attempt + 1}/${RETRY_CONFIG.maxRetries + 1}), retrying in ${delay}ms...`, {
        url: config.url,
        error: error.message,
        status: error.response?.status
      });
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryRequest(config, attempt + 1);
    }
    
    // Si no es reintentable o es el último intento, rechazar la promesa
    throw error;
  }
};

// Rutas que no requieren reintentos
const omitRoutes = [
  '/refreshToken',
  '/recoveryPassword',
  '/logout',
  '/confirmAccount',
  '/resetPassword',
];

// Rutas externas
const externalRoutes = [];

// Función principal de API
const apiRequest = async (url, options = {}) => {
  let user = localStorage.getItem('x-auth-user');
  if (user) user = JSON.parse(user);
  
  // Obtener idioma del navegador o localStorage
  const browserLanguage = navigator.language || navigator.languages[0];
  const storedLanguage = localStorage.getItem('i18nextLng');
  const locale = storedLanguage || browserLanguage || 'en';

  let config = {
    ...options,
    timeout: 30000, // 30 segundos timeout
    headers: {
      ...options.headers,
      'Accept-Language': locale,
      clientId: env.clientId,
      'Content-Type': 'application/json'
    },
    baseURL: env.apiUrl,
    url: url
  };

  // Configurar autenticación
  if (url.includes('/auth') && !url.includes('changePassword') && !url.includes('logout')) {
    if (omitRoutes.every((str) => !url.includes(str))) {
      const { username, password } = options.data;
      config.headers.Authorization = `Basic ${btoa(`${username}:${password}`)}`;
      delete config.data;
    }
  } else if (externalRoutes.every((str) => !url.includes(str))) {
    const token = localStorage.getItem('x-auth-token');
    if (token) {
      const { tokenType, accessToken } = JSON.parse(token);
      config.headers.Authorization = `${tokenType} ${accessToken}`;
    } else {
      window.location.reload();
    }
  }

  try {
    const response = await retryRequest(config);
    return response;
  } catch (error) {
    // Manejo especial para errores 401 (token expirado)
    if (error.response?.status === 401) {
      try {
        const token = JSON.parse(localStorage.getItem("x-auth-token"));
        localStorage.removeItem("x-auth-token");
        if (token) {
          const { accessToken, refreshToken } = token;
          if (accessToken && refreshToken) {
            const refreshResponse = await apiRequest("/auth/refreshToken", {
              method: "POST",
              data: { accessToken, refreshToken }
            });
            localStorage.setItem("x-auth-token", JSON.stringify(refreshResponse.data));
            // Reintentar la petición original
            return apiRequest(url, options);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        window.location.reload();
      }
    }
    
    throw error;
  }
};

// Función para crear un cliente HTTP con configuración personalizada
export const createApiClient = (baseConfig = {}) => {
  return {
    get: (url, config = {}) => apiRequest(url, { ...baseConfig, ...config, method: 'GET' }),
    post: (url, data, config = {}) => apiRequest(url, { ...baseConfig, ...config, method: 'POST', data }),
    put: (url, data, config = {}) => apiRequest(url, { ...baseConfig, ...config, method: 'PUT', data }),
    patch: (url, data, config = {}) => apiRequest(url, { ...baseConfig, ...config, method: 'PATCH', data }),
    delete: (url, config = {}) => apiRequest(url, { ...baseConfig, ...config, method: 'DELETE' })
  };
};

export default apiRequest;
